/**
 * OPA / SCT arithmetic — the browser's copy.
 *
 * Transcribed from the formulas inside `docs/S27 FORMAT.xlsx`, not inferred from its numbers:
 *
 *     J6  Skin CT (h)            = ROUND(skin total seconds / 3600, 2)
 *     J5  Variable Sewing Cycles = ROUND(skin + case + acc1 + acc2, 2)
 *     J11 Frame-fit CT (h)       = ROUND(minutes / 60, 2)      … and the same for J12, J13
 *     J10 Standard Work Subtotal = ROUND(frame-fit + frame ass. + packaging, 2)
 *     J4  SCT(h)                 = ROUND((J5 / J14 + J10) * (1 + J15), 2)
 *
 * ROUNDED AT EVERY STEP because the workbook is. Carrying full precision through and rounding once
 * at the end gives 1.5225 for CHAIR ONE where the file gives 1.52 — the same to two places here, but
 * not on a style whose intermediate lands on a half, and the file is the authority.
 *
 * This is the second copy; the server has the other (`opaTotals` in server.js) for the saved list and
 * the export. Both cite the workbook and both are checked against CHAIR ONE S27, whose figures are
 * skin 0.71, case 0.30, variable 1.01, standard 0.03 and SCT 1.52.
 */
/* Seven variable blocks, not four (Yến via Max, 2026-09-01). Her ROYAL BOX SHADE already fills four
   — Skin, Case, Pole Case, Peg Case — and she has styles running to ACC4, so ACC5 is the spare she
   asked for. A document still prints its own wording per block (sectionTitles), which is how hers
   says "Pole Case" where the format says ACC1. */
export const OPA_SECTIONS = ['SKIN', 'CASE', 'ACC1', 'ACC2', 'ACC3', 'ACC4', 'ACC5'];
export const OPA_SECTION_LABEL = {
  SKIN: 'I. SKIN', CASE: 'II. CASE', ACC1: 'III. ACC 1', ACC2: 'IV. ACC 2',
  ACC3: 'V. ACC 3', ACC4: 'VI. ACC 4', ACC5: 'VII. ACC 5',
};

const num = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
/* Excel rounds half away from zero on the DECIMAL value; JavaScript rounds the binary one, and
   0.575 * 100 is 57.49999999999999 in binary — so CHAIR ONE (Re)'s skin came out 0.57 against the
   workbook's 0.58 (2026-08-30). Trimming to 12 significant digits restores the decimal the sheet
   shows before the rounding decision is made. */
export const opaRound = (v) => {
  const n = Number(v) || 0;
  const scaled = Number((Math.abs(n) * 100).toPrecision(12));
  return (n < 0 ? -1 : 1) * (Math.round(scaled) / 100);
};

/** One operation row: quantity x seconds per part, exactly as column J does it (J = H * I). */
export const opaLineSeconds = (line) => num(line?.partQty) * num(line?.ctPerPartSec);

/** Seconds per section for a flat list of lines. */
export const opaSeconds = (lines) => {
  const out = Object.fromEntries(OPA_SECTIONS.map((k) => [k, 0]));
  (lines || []).forEach((l) => {
    if (out[l.section] === undefined) return;
    out[l.section] += opaLineSeconds(l);
  });
  return out;
};

/* A section can describe ONE of a part the product carries several of: HELITOSS heads its first
   block "I. RING X 1" and its ladder reads =ROUND($J$20*6/3600,2) — six rings per game — and
   HELIDROP does the same with three bean bags. The operations stay per-piece; the count multiplies
   the section's hours. Anything else is 1, which is every other sheet in the S27 folder. */
export const opaSectionMult = (head, sec) => {
  const raw = Number(head?.sectionMult?.[sec]);
  return Number.isFinite(raw) && raw > 0 ? raw : 1;
};

/** Everything the header block shows. `head` carries the rates; standard work arrives in MINUTES. */
export const opaTotals = (head, seconds) => {
  const hours = {};
  OPA_SECTIONS.forEach((s) => {
    hours[s] = opaRound(((seconds?.[s] || 0) * opaSectionMult(head, s)) / 3600);
  });
  const variable = opaRound(OPA_SECTIONS.reduce((sum, s) => sum + hours[s], 0));
  const frameFit = opaRound(num(head?.frameFitMin) / 60);
  const frameAss = opaRound(num(head?.frameAssMin) / 60);
  const packaging = opaRound(num(head?.packagingMin) / 60);
  const standard = opaRound(frameFit + frameAss + packaging);
  // Efficiency divides. Zero would put Infinity in a costing sheet, so it is read as 1 — no
  // adjustment — which is what the blank format ships with.
  const eff = num(head?.styleEfficiency) > 0 ? num(head.styleEfficiency) : 1;
  const sct = opaRound((variable / eff + standard) * (1 + num(head?.indirectRate)));
  return { hours, variable, frameFit, frameAss, packaging, standard, sct };
};
